/**
 * Fans (`/fans`) — dezelfde tabel, filters en paginering als de huidige pagina.
 *
 * Endpoint exact zoals de bundle:
 *   /api/admin/fans?limit=100&offset=0&sort_by=last_active_at&sort_order=desc
 * Alle fans zijn bereikbaar via de paginering (8.579 op dit moment).
 */
import { useEffect, useMemo, useState } from 'react'
import { ErrorNote, Loading, Panel, Plat, Seg, Table, Tag } from '../components/ui'
import { useApi } from '../lib/useApi'
import { useActions } from '../lib/actions'
import { dt, eur, num } from '../lib/format'
import { ModelNaam } from '../components/ModelNaam'
import { ModelFilter, useModelFilter } from '../components/ModelFilter'
import { useNavigate } from 'react-router-dom'

interface Fan {
  id: number
  fan_username: string | null
  fan_display_name: string | null
  fan_platform_id: string | null
  platform: string
  platforms: string[] | null
  model_slug: string | null
  model_slugs: string[] | null
  model_count: number
  engagement_level: string | null
  total_spend: number
  is_excluded_from_ai: boolean
  is_vip: boolean
  conversation_count: number
  first_seen_at: string | null
  last_active_at: string | null
}
interface Page<T> { data: T[]; total: number }

const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]
const SIZES = [25, 50, 100, 200]
const COLS = [
  { key: 'id', label: 'ID' },
  { key: 'user', label: 'Username' },
  { key: 'plat', label: 'Platform' },
  { key: 'model', label: 'Model' },
  { key: 'lvl', label: 'Level' },
  { key: 'spend', label: 'Spend (all)', num: true },
  { key: 'ai', label: 'AI' },
  { key: 'chats', label: 'Chats', num: true },
  { key: 'first', label: 'First Seen' },
  { key: 'last', label: 'Last Active' },
  { key: 'act', label: '' },
]
const LEVEL_KIND: Record<string, 'ok' | 'warn' | 'bad' | 'acc' | 'mut'> = {
  hot: 'bad', warm: 'warn', vip: 'acc', buyer_intent: 'ok', cold: 'mut', low: 'mut',
}

/** "Model" toont één slug, of "n models" als de fan er meer heeft. */
function modelLabel(f: Fan): string {
  const n = f.model_count ?? (f.model_slugs?.length ?? 0)
  if (n > 1) return `${n} models`
  return f.model_slug || f.model_slugs?.[0] || '—'
}

export default function Fans() {
  const navigate = useNavigate()
  const [platform, setPlatform] = useState('all')
  // 04-10-2026 (Jordy): gedeeld modelfilter (FC+F2F samen, in het adres). Het
  // oude filter werkte alleen op de geladen pagina van 100 fans; nu filtert de
  // server, dus ook fans die niet op de eerste pagina staan.
  const filter = useModelFilter()
  const [search, setSearch] = useState('')
  const [size, setSize] = useState(100)
  const [page, setPage] = useState(0)
  const { run } = useActions()

  const modelParam = filter.waarde === 'all' ? ''
    : `&model_slug=${encodeURIComponent(filter.slugs ? filter.slugs.join(',') : '__laden__')}`
  const q = useApi<Page<Fan>>(
    `/api/admin/fans?limit=${size}&offset=${page * size}` +
    '&sort_by=last_active_at&sort_order=desc' + modelParam,
    [size, page, modelParam],
  )
  useEffect(() => { setPage(0) }, [modelParam])

  const rows = useMemo(() => {
    let list = q.data?.data ?? []
    if (platform !== 'all') {
      list = list.filter(
        (f) => f.platform === platform || (f.platforms ?? []).includes(platform))
    }
    const needle = search.trim().toLowerCase()
    if (needle) {
      list = list.filter((f) =>
        [f.fan_username, f.fan_display_name, f.fan_platform_id, f.model_slug]
          .some((v) => String(v ?? '').toLowerCase().includes(needle)))
    }
    return list
  }, [q.data, platform, search])

  const total = q.data?.total ?? 0
  const from = total ? page * size + 1 : 0
  const to = Math.min((page + 1) * size, total)

  return (
    <>
      <div className="filters">
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        <ModelFilter filter={filter} />
        <input
          className="inp"
          style={{ maxWidth: 240 }}
          placeholder="Search fans…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="spacer" style={{ flex: 1 }} />
        <select className="select" value={size}
                onChange={(e) => { setSize(Number(e.target.value)); setPage(0) }}>
          {SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <button
          className="btn ghost sm"
          type="button"
          onClick={() => run({
            label: 'Export CSV',
            request: async () => {
              // Zelfde kolommen als de tabel; client-side, zoals de bundle.
              const head = ['ID','Username','Platform','Model','Level','Spend','AI','Chats','First Seen','Last Active']
              const lines = rows.map((f) => [
                f.id, f.fan_username ?? f.fan_display_name ?? f.fan_platform_id ?? '',
                f.platform, modelLabel(f), f.engagement_level ?? '',
                f.total_spend, f.is_excluded_from_ai ? 'off' : 'on',
                f.conversation_count, f.first_seen_at ?? '', f.last_active_at ?? '',
              ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
              const blob = new Blob([[head.join(','), ...lines].join('\n')],
                                    { type: 'text/csv;charset=utf-8' })
              const a = document.createElement('a')
              a.href = URL.createObjectURL(blob)
              a.download = `fans-${new Date().toISOString().slice(0, 10)}.csv`
              a.click()
              URL.revokeObjectURL(a.href)
            },
            successText: `${rows.length} fans exported`,
          })}
        >
          Export CSV
        </button>
      </div>

      <Panel
        title="Fans"
        desc="Sorted by last active"
        flush
        right={
          <>
            <span className="mut2" style={{ fontSize: 12 }}>
              {from}–{to} of {num(total)}
            </span>
            <button className="btn ghost sm" type="button" disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}>← Previous</button>
            <button className="btn ghost sm" type="button"
                    disabled={(page + 1) * size >= total}
                    onClick={() => setPage((p) => p + 1)}>Next →</button>
          </>
        }
      >
        {q.loading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : (
          <Table cols={COLS} emptyText="No fans match this filter">
            {rows.map((f) => (
              /* Klik op de rij opent het fandetail, zoals oud. */
              <tr key={f.id} className="clickrow"
                  onClick={() => navigate(`/fans/show/${f.id}`)}>
                <td className="mut2 mono">{f.id}</td>
                <td>
                  <b>{f.fan_display_name || f.fan_username || f.fan_platform_id}</b>
                  {f.fan_username && f.fan_display_name ? (
                    <span className="mut2" style={{ fontSize: 12 }}> {f.fan_username}</span>
                  ) : null}
                </td>
                <td><Plat value={f.platform} /></td>
                <td>{(f.model_count ?? (f.model_slugs?.length ?? 0)) > 1
                  ? <span className="mut">{modelLabel(f)}</span>
                  : <ModelNaam slug={f.model_slug || f.model_slugs?.[0]} />}</td>
                <td>
                  {f.is_vip ? <Tag kind="acc">vip</Tag>
                    : f.engagement_level
                      ? <Tag kind={LEVEL_KIND[f.engagement_level] || 'mut'}>{f.engagement_level}</Tag>
                      : <span className="dim">—</span>}
                </td>
                <td className="num mono">{eur(f.total_spend)}</td>
                <td>{f.is_excluded_from_ai ? <Tag kind="mut">Off</Tag> : <Tag kind="ok">On</Tag>}</td>
                <td className="num mono">{num(f.conversation_count)}</td>
                <td className="mut2" style={{ fontSize: 12 }}>{dt(f.first_seen_at)}</td>
                <td className="mut2" style={{ fontSize: 12 }}>{dt(f.last_active_at)}</td>
                <td>
                  <button className="btn ghost sm" type="button"
                          onClick={() => navigate(
                            `/conversations?fan=${encodeURIComponent(f.fan_platform_id || f.fan_username || '')}`)}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </>
  )
}
